/**
 * @file Provides Auth0 configuration retrieval for different environments.
 */
import { environments } from './config';

/**
 * Retrieves the Auth0 configuration based on the current execution environment. This function
 * looks up the environment settings from a predefined configuration object and validates
 * the presence of necessary Auth0 parameters such as domain, client ID, and redirect URI.
 * If any of these parameters are missing, it logs an error and returns `null` to prevent
 * runtime errors in the authentication flow.
 *
 * @returns {?Object} The Auth0 configuration object containing `domain`, `clientId`, and `redirectUri`,
 *                    or `null` if the configuration is invalid.
 */
export const getAuth0Config = () => {
    const env = process.env.NODE_ENV;
    const auth0_domain = process.env.REACT_APP_AUTH0_DOMAIN;
    const auth0_scope = 'openid profile email';

    if (env === 'development') {
        return {
            domain: auth0_domain,
            clientId: environments.development.AUTH0_CLIENT_ID,
            redirectUri: environments.development.AUTH0_LOGIN_REDIRECT_URI,
            audience: environments.development.AUTH0_LOGIN_AUDIENCE,
            scope: auth0_scope
        };
    } else if (env === 'production') {
        const hostname = window.location.hostname;
        const stages = Object.keys(environments.production);
        for (const key of stages) {
            const stageConfig = environments.production[key];
            const subDomains = stageConfig.SUBDOMAIN.split(',');
            const domains = stageConfig.DOMAIN.split(',');
            for (const domain of domains) {
                for (const subDomain of subDomains) {
                    if (hostname === `${subDomain}.${domain}`) {
                        return {
                            domain: auth0_domain,
                            clientId: stageConfig.AUTH0_CLIENT_ID,
                            audience: stageConfig.AUTH0_LOGIN_AUDIENCE,
                            redirectUri:
                                stageConfig.AUTH0_LOGIN_REDIRECT_URI.replace(
                                    '--subdomain--',
                                    subDomain
                                ).replace('--domain--', domain),
                            scope: auth0_scope
                        };
                    }
                }
            }
        }
        // Log an error or handle the absence of a correct configuration
        console.error(`No Auth0 configuration found for hostname: ${hostname}`);
        return null;
    }
    console.error(`No environment configuration found for NODE_ENV: ${env}`);
    return null;
};

/** @function
 * Get the API config from environments.
 * @returns {{SUBDOMAIN: string, AUTH0_CLIENT_ID: string, API_KEY: string, HEAP_APP_ID: string, AUTH0_LOGIN_REDIRECT_URI: string}}
 */
export function getApiConfig() {
    if (process.env.NODE_ENV === 'development') {
        const config = environments.development;
        return { ...config, SUBDOMAIN: 'test' };
    } else {
        return getProductionApiConfig();
    }
}

/** @function
 * Gets production configs from environments.
 * @returns {*&{SUBDOMAIN: string, AUTH0_LOGIN_AUDIENCE: string, AUTH0_LOGIN_REDIRECT_URI: string}} */
function getProductionApiConfig() {
    const hostname = window.location.hostname;

    for (const config of Object.values(environments.production)) {
        for (const domain of config.DOMAIN.split(',')) {
            for (const subDomain of config.SUBDOMAIN.split(',')) {
                if (hostname === `${subDomain}.${domain}`) {
                    return {
                        ...config,
                        AUTH0_LOGIN_REDIRECT_URI:
                            config.AUTH0_LOGIN_REDIRECT_URI.replace(
                                '--subdomain--',
                                subDomain
                            ).replace('--domain--', domain),
                        SUBDOMAIN: subDomain,
                        DOMAIN: domain
                    };
                }
            }
        }
    }
    return undefined;
}

function getSiteIndexUrl(apiConfig) {
    return 'https://' + apiConfig.SUBDOMAIN + '.' + apiConfig.DOMAIN + '/';
}

/** @function
 * Transforms the API URI from the relevant config.
 * @param apiConfig
 * @returns {string}
 */
export function getApiUri(apiConfig) {
    const path = process.env.REACT_APP_API_PATH;
    return getSiteIndexUrl(apiConfig) + path;
}

/** @function
 * Transforms the terms-of-use link from the relevant config.
 * @param apiConfig
 * @returns {string}
 */
export function getTermsAndConditions(apiConfig) {
    return getSiteIndexUrl(apiConfig) + 'terms-of-use';
}
